import { useState } from 'react'
import { lazy, Suspense } from 'react'
import { SpeechGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/SpeechPage.stylex'

const SpeechPopupRaw = lazy(() => import('@/components/popup/SpeechPopup'));
const SpeechPopup = (props: any) => (
    <Suspense fallback={null}>
        <SpeechPopupRaw {...props} />
    </Suspense>
);

/**
 * Sub-component to manage individual hover states for StyleX logic
 */
function SpeechGalleryItemComponent({
    item,
    onOpen
}: {
    item: SpeechGalleryItem,
    onOpen: (item: SpeechGalleryItem) => void
}) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            {...stylex.props(styles.speechGalleryItem)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Voice Name and Click Button Container */}
            <div {...stylex.props(styles.voiceInfoContainer)}>
                <div {...stylex.props(styles.voiceName)}>{item.voice}</div>
                {item.voice2 && <div {...stylex.props(styles.voiceName)}>& {item.voice2}</div>}

                <div
                    {...stylex.props(
                        styles.clickIndicatorSpeech,
                        isHovered && styles.indicatorVisible
                    )}
                    onClick={() => onOpen(item)}
                >
                    Click Me!
                </div>
            </div>

            {/* Audio Player */}
            <div {...stylex.props(styles.audioPlayerContainer)} onClick={(e) => e.stopPropagation()}>
                <audio controls {...stylex.props(styles.audioPlayer)}>
                    <source src={item.audioSrc} type="audio/mpeg" />
                    <source src={item.audioSrc} type="audio/wav" />
                    <source src={item.audioSrc} type="audio/ogg" />
                    Your browser does not support the audio element.
                </audio>
            </div>
        </div>
    );
}

interface TextToSpeechGalleryProps {
    title: string
    description: string
    modelKey: string
    modelLabel: string
    modelNote: string
    items: SpeechGalleryItem[]
}

export function TextToSpeechGallery({ title, description, modelKey, modelLabel, modelNote, items }: TextToSpeechGalleryProps) {
    const [showPopup, setShowPopup] = useState(false)
    const [selectedItem, setSelectedItem] = useState<SpeechGalleryItem>({ id: '', audioSrc: '', className: '', prompt: '' })

    const openPopup = (item: SpeechGalleryItem) => {
        setSelectedItem(item)
        setShowPopup(true)
    }

    const closePopup = () => {
        setShowPopup(false)
    }

    return (
        <>
            <div {...stylex.props(styles.gallerySection)}>
                <h3 {...stylex.props(styles.sectionTitle)}>{title}</h3>
                <p
                    {...stylex.props(styles.galleryDescription)}
                    dangerouslySetInnerHTML={{ __html: description }}
                />

                <div {...stylex.props(styles.speechGalleryList)}>
                    {items.map((item, index) => (
                        <SpeechGalleryItemComponent
                            key={index}
                            item={item}
                            onOpen={openPopup}
                        />
                    ))}
                </div>
            </div>

            {/* Speech Popup with gallery item data */}
            <SpeechPopup
                isOpen={showPopup}
                onClose={closePopup}
                id={selectedItem.id}
                audioSrc={selectedItem.audioSrc}
                modelKey={modelKey}
                modelLabel={modelLabel}
                prompt={selectedItem.prompt}
                voice={selectedItem.voice}
                voiceDescription={selectedItem.voiceDescription}
                voice2={selectedItem.voice2}
                voice2Description={selectedItem.voice2Description}
                tone={selectedItem.tone}
                popupTitle="Speech Details"
                modelNote={modelNote}
            />
        </>
    )
}